import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowLeftRight, Camera, Loader2, Printer, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  type ItemView,
  type PalletSummary,
  type SessionDetail,
  createOrderCheckSession,
  kindSuffix,
  deleteItem,
  deletePallet,
  printPalletLabel,
  deletePalletPhoto,
  fetchPalletPhotoUrl,
  getOrderCheckConfig,
  getOrderCheckSession,
  listPalletPhotos,
  moveItem,
  stagePalletPhoto,
} from "@/api/order_check";
import { PhotoCapture } from "@/components/PhotoCapture";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { BulkDeleteError, deleteItemsInOrder, plural, useOrderKind } from "@/lib/order-check";
import { useUploadProgress } from "@/lib/use-upload-progress";
import { CharacteristicBadge } from "@/pages/order-check/v2/CharacteristicBadge";
import { EmptyPalletBadge } from "@/pages/order-check/v2/EmptyPallet";
import { palletNumber } from "@/pages/order-check/v2/pallet-number";
import { cn } from "@/lib/utils";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { fmtQty } from "@/lib/scan-ui/status";

export function PalletPage(): React.ReactElement {
  const { ref, palletId } = useParams<{ ref: string; palletId: string }>();
  const kind = useOrderKind();
  const navigate = useNavigate();
  const sessionQ = useQuery({ queryKey: ["ocv2-session", ref, kind], queryFn: () => createOrderCheckSession(ref!, kind), enabled: !!ref });
  const sessionId = sessionQ.data?.id;
  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      {!sessionId ? (
        <div className="flex flex-1 items-center justify-center text-slate-400"><Loader2 className="size-7 animate-spin" /></div>
      ) : (
        <PalletBody sessionId={sessionId} palletId={palletId!} onBack={() => navigate(`/order-check/orders/${ref}${kindSuffix(kind)}`)} />
      )}
    </div>
  );
}

function PalletBody({ sessionId, palletId, onBack }: { sessionId: string; palletId: string; onBack: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const detailKey = ["ocv2-detail", sessionId];
  const detailQ = useQuery({ queryKey: detailKey, queryFn: () => getOrderCheckSession(sessionId) });
  const photosQ = useQuery({ queryKey: ["ocv2-photos", sessionId], queryFn: () => listPalletPhotos(sessionId), staleTime: 30000 });
  const refetch = () => {
    qc.invalidateQueries({ queryKey: detailKey });
  };
  const detail = detailQ.data;
  const pallet = detail?.pallets.find((p) => p.id === palletId);
  // Переносить можно только на паллету, в которую эта сессия вправе писать:
  // на отгруженную/чужую бэкенд перенос всё равно отобьёт.
  const others = (detail?.pallets ?? []).filter((p) => p.id !== palletId && p.writable);
  const completed = detail?.header.status === "completed";
  // Неизменяема паллета завершённой сессии и любая, в которую бэкенд писать не
  // даст (`writable` — отгружена, аннулирована, взята новой проверкой заказа:
  // `cargo.assert_writable`). Номер на этикетке про изменяемость не говорит.
  const readOnly = completed || !pallet?.writable;

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [delPalOpen, setDelPalOpen] = useState(false);

  const myPhotos = (photosQ.data ?? []).filter((ph) => ph.pallet_id === palletId);

  const delItemM = useMutation({
    mutationFn: (id: string) => deleteItem(sessionId, id),
    onSuccess: () => { toast.success("Позиция удалена"); refetch(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка удаления")),
  });
  const configQ = useQuery({ queryKey: ["oc-config"], queryFn: getOrderCheckConfig, staleTime: 300000 });
  const printerReady = configQ.data?.pallet_printer_ready ?? false;
  const printM = useMutation({
    mutationFn: () => printPalletLabel(sessionId, palletId),
    onSuccess: () => { toast.success("Этикетка отправлена на принтер"); refetch(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось напечатать этикетку")),
  });
  const delPalM = useMutation({
    mutationFn: () => deletePallet(sessionId, palletId),
    onSuccess: () => { toast.success("Паллета удалена"); onBack(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка удаления паллеты")),
  });
  // Процент отправки снимка — общий хук: ритуал «обнулить → отслеживать →
  // сбросить» одинаков во всех экранах со съёмкой, и пропущенная часть даёт
  // экран, который выглядит рабочим, но прогресса не показывает.
  const photoProgress = useUploadProgress();
  const stagePhotoM = useMutation({
    mutationFn: (blob: Blob) =>
      stagePalletPhoto(sessionId, palletId, blob, photoProgress.start()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ocv2-photos", sessionId] }); toast.success("Фото сохранено"); setPhotoOpen(false); },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка фото")),
    onSettled: photoProgress.reset,
  });
  const delPhotoM = useMutation({
    mutationFn: (id: string) => deletePalletPhoto(sessionId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ocv2-photos", sessionId] }),
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка")),
  });

  async function moveTo(targetId: string) {
    const ids = [...selected];
    let done = 0;
    try {
      for (const id of ids) {
        await moveItem(sessionId, id, targetId);
        done++;
      }
      toast.success(`Перенесено ${done} из ${ids.length}`);
    } catch (e) {
      toast.error(`Перенесено ${done} из ${ids.length}. Ошибка: ${extractErrorMessage(e, "")}`);
    }
    setMoveOpen(false);
    setSelectMode(false);
    setSelected(new Set());
    refetch();
  }

  // По одной: строка сборной коробки снимает всю коробку, и что именно снято —
  // знает только ответ сервера (`deleteItemsInOrder`). Сбой на середине — тост
  // с числом снятого; выбор сбрасываем и перечитываем паллету в любом случае.
  async function deleteSelected() {
    const ids = [...selected];
    try {
      const removed = await deleteItemsInOrder(ids, (id) => deleteItem(sessionId, id));
      toast.success(`Удалено ${removed} ${plural(removed, ["скан", "скана", "сканов"])}`);
    } catch (e) {
      const done = e instanceof BulkDeleteError ? e.removed : 0;
      const cause = e instanceof BulkDeleteError ? e.cause : e;
      toast.error(`Удалено ${done}, дальше ошибка: ${extractErrorMessage(cause, "")}`);
    }
    setSelectMode(false);
    setSelected(new Set());
    refetch();
  }

  if (!detail || !pallet) {
    return <div className="flex flex-1 items-center justify-center text-slate-400"><Loader2 className="size-7 animate-spin" /></div>;
  }

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  return (
    <>
      <div className="flex h-[46px] items-center gap-1 bg-slate-900 px-1 text-white">
        <button type="button" onClick={onBack} className="flex size-9 items-center justify-center"><ArrowLeft className="size-5" /></button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[14px] font-bold">
            {pallet.label}
            {pallet.items_count === 0 && <EmptyPalletBadge />}
          </div>
          <div className="truncate text-[10px] text-slate-400">
            {pallet.items_count} поз · {fmtQty(pallet.qty)} шт{pallet.boxes > 0 ? ` · ${fmtQty(pallet.boxes)} кор` : ""}
            {palletNumber(pallet) ? ` · ${palletNumber(pallet)}` : ""}{pallet.shipped ? " · отгружена" : readOnly ? " · read-only" : ""}
          </div>
        </div>
        {!readOnly && pallet.items.length > 0 && (
          <button type="button" onClick={() => { setSelectMode((s) => !s); setSelected(new Set()); }} className={cn("mr-1 rounded px-2 py-1 text-[12px]", selectMode ? "bg-sky-700" : "bg-white/10")}>
            {selectMode ? "Готово" : "Выбрать"}
          </button>
        )}
      </div>

      {/* Почему паллету только смотрят — один раз, приглушённо: каждая кнопка
          отдельно об этом не говорит, она просто скрыта. */}
      {pallet.lock_reason && (
        <div className="border-b bg-slate-100 px-3 py-1.5 text-[12px] text-slate-500">{pallet.lock_reason}</div>
      )}

      {/* Этикетка доступна и у завершённой сессии: её мнут, заливают и срывают,
          а номер от перепечатки не меняется. Отгруженную, аннулированную и
          взятую новой проверкой не печатаем — бэкенд отобьёт (`assert_writable`
          у ручки этикетки); у завершённой сессии `writable` ложно из-за статуса
          сессии, поэтому её паллеты пускаем по `!shipped`, как прежде. */}
      {palletNumber(pallet) && (pallet.writable || (completed && !pallet.shipped)) && (
        <div className="border-b bg-white p-2">
          <Button
            variant={pallet.printed_at ? "outline" : "default"}
            className="h-11 w-full"
            disabled={printM.isPending || !printerReady}
            onClick={() => printM.mutate()}
          >
            <Printer className="size-4" />
            {pallet.printed_at ? "Перепечатать этикетку" : "Напечатать этикетку"}
          </Button>
          {/* Принтер паллет — отдельная настройка: он стоит на складе, а не в
              комнате ФБС. Не настроен — гасим кнопку и говорим почему, вместо
              того чтобы отправить этикетку в никуда. */}
          {!printerReady && (
            <div className="mt-1 text-center text-xs text-amber-700">
              Принтер этикеток паллет не настроен — задайте его в системных настройках
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="flex gap-1.5 border-b bg-white p-2">
          <Button variant="outline" className="h-11 flex-1" onClick={() => setPhotoOpen(true)}>
            <Camera className="size-4" /> {myPhotos.length ? "Ещё фото" : "Сделать фото"}
          </Button>
          <Button variant="outline" className="h-11 w-12 border-red-200 text-red-700" onClick={() => (pallet.items_count > 0 ? setDelPalOpen(true) : delPalM.mutate())}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}

      {myPhotos.length > 0 && (
        <div className="border-b bg-white px-2 py-1.5">
          <div className="text-[10px] font-bold text-slate-400">Фото паллеты (черновик · уйдут в 1С при завершении)</div>
          <div className="mt-1 flex gap-2 overflow-x-auto">
            {myPhotos.map((ph) => (
              <PhotoThumb key={ph.id} sessionId={sessionId} photoId={ph.id} onDelete={readOnly ? undefined : () => delPhotoM.mutate(ph.id)} />
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 space-y-1.5 overflow-y-auto p-2 pb-24">
        {pallet.items.length === 0 && <div className="py-6 text-center text-[12px] text-slate-400">Отсканируйте товары на эту паллету.</div>}
        {pallet.items.map((it) => (
          <ItemRow key={it.id} item={it} selectMode={selectMode && !readOnly} selected={selected.has(it.id)} onToggle={() => toggle(it.id)} onDelete={readOnly ? undefined : () => delItemM.mutate(it.id)} />
        ))}
      </div>

      {selectMode && !readOnly && selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 mx-auto flex max-w-md items-center gap-2 bg-slate-900 p-2 text-white">
          <span className="flex-1 pl-1 text-[13px]">Выбрано: {selected.size}</span>
          <Button className="h-11 bg-sky-700" disabled={others.length === 0} onClick={() => setMoveOpen(true)}>
            <ArrowLeftRight className="size-4" /> Перенести ({selected.size})
          </Button>
          <Button className="h-11 bg-red-700" aria-label="Удалить выбранные" onClick={() => void deleteSelected()}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}

      {moveOpen && !readOnly && <MoveSheet others={others} onPick={moveTo} onClose={() => setMoveOpen(false)} count={selected.size} />}
      {photoOpen && !readOnly && (
        <PhotoCapture
          title={`Сфотографируйте ${pallet.label}`}
          busy={stagePhotoM.isPending}
          progress={photoProgress.progress}
          onCapture={(b) => stagePhotoM.mutate(b)}
          onClose={() => setPhotoOpen(false)}
        />
      )}
      {delPalOpen && !readOnly && (
        <Dialog
          title={`Удалить ${pallet.label}?`}
          body={`На паллете ${pallet.items_count} поз · ${fmtQty(pallet.qty)} шт. Позиции будут удалены из проверки. Продолжить?`}
          confirmLabel="Удалить"
          danger
          confirmBusy={delPalM.isPending}
          onConfirm={() => delPalM.mutate()}
          onCancel={() => setDelPalOpen(false)}
        />
      )}
    </>
  );
}

export function ItemRow({ item, selectMode, selected, onToggle, onDelete }: { item: ItemView; selectMode: boolean; selected: boolean; onToggle: () => void; onDelete?: () => void }): React.ReactElement {
  const coef = item.qty_scanned && item.qty_base ? item.qty_base / item.qty_scanned : 1;
  return (
    <div className={cn("flex items-start gap-2 rounded-lg border p-2", selected ? "border-sky-300 bg-sky-50" : "bg-white")}>
      {selectMode && <input type="checkbox" checked={selected} onChange={onToggle} className="mt-1 size-5" />}
      <button type="button" onClick={selectMode ? onToggle : undefined} className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-center gap-1">
          <span className="font-mono text-[14px] font-bold">{item.nomenclature_article}</span>
          <CharacteristicBadge name={item.characteristic_name} characteristicRef={item.characteristic_ref} />
          {item.partia_name ? <span className="rounded border bg-white px-1 font-mono text-[10px]">парт. {item.partia_name}</span> : <span className="rounded border bg-white px-1 text-[10px] text-slate-400">без партии</span>}
          {coef > 1 && <span className="rounded border border-sky-200 bg-sky-100 px-1 text-[10px] text-sky-800">короб ×{fmtQty(coef)}</span>}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-600">{item.nomenclature_name}</div>
        <div className="text-[11px] text-slate-500">{fmtQty(item.qty_base)} шт{item.boxes > 0 ? ` · ${fmtQty(item.boxes)} кор.` : " · россыпь"}</div>
      </button>
      {!selectMode && onDelete && (
        <button type="button" onClick={onDelete} className="flex size-9 items-center justify-center text-slate-400 hover:text-red-600">
          <Trash2 className="size-4" />
        </button>
      )}
    </div>
  );
}

function PhotoThumb({ sessionId, photoId, onDelete }: { sessionId: string; photoId: string; onDelete?: () => void }): React.ReactElement {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    let objUrl = "";
    fetchPalletPhotoUrl(sessionId, photoId)
      .then((x) => {
        objUrl = x;
        if (cancelled) URL.revokeObjectURL(x); // размонтировались, пока грузилось
        else setUrl(x);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [sessionId, photoId]);
  return (
    <div className="relative shrink-0">
      <div className="size-16 overflow-hidden rounded-lg border bg-slate-100">
        {url && <img src={url} alt="" className="size-full object-cover" />}
      </div>
      {onDelete && (
        <button type="button" onClick={onDelete} className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-red-700 text-[10px] text-white">
          ✕
        </button>
      )}
    </div>
  );
}

function MoveSheet({ others, onPick, onClose, count }: { others: PalletSummary[]; onPick: (id: string) => void; onClose: () => void; count: number }): React.ReactElement {
  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[80%] overflow-y-auto">
      <div className="border-b p-3 text-[15px] font-bold">Куда перенести {count} {plural(count, ["скан", "скана", "сканов"])}</div>
      <div className="space-y-1.5 p-2">
        {others.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p.id)} className="flex min-h-[56px] w-full items-center justify-between rounded-lg border bg-white p-3 text-left">
            <span className="text-[14px] font-bold">{p.label}</span>
            <span className="text-[12px] text-slate-500">{p.items_count} поз · {fmtQty(p.qty)} шт</span>
          </button>
        ))}
      </div>
      <div className="px-3 pb-3 text-[11px] text-slate-400">Новую паллету здесь не создаём — вернитесь на скан.</div>
    </BottomSheet>
  );
}

// используется для сверки типов
export type { SessionDetail };
